import { useEffect, useState } from "react";
import { Loader2, Trash2, EyeOff, Eye, Ban, Check, Save, Coins, Plus, Minus, Instagram } from "lucide-react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

export default function AdminCommunity() {
  return (
    <div>
      <h1 className="font-display text-3xl font-bold uppercase tracking-tight">Community</h1>
      <p className="mt-1 text-sm text-zinc-400">Credit-based Instagram support system. Members earn credits by supporting other barbers.</p>
      <Tabs defaultValue="members" className="mt-6">
        <TabsList className="bg-zinc-900 border border-white/10">
          <TabsTrigger value="members" data-testid="admin-tab-members">Members</TabsTrigger>
          <TabsTrigger value="posts" data-testid="admin-tab-posts">Posts</TabsTrigger>
          <TabsTrigger value="settings" data-testid="admin-tab-settings">Settings</TabsTrigger>
        </TabsList>
        <TabsContent value="members" className="mt-6"><Members /></TabsContent>
        <TabsContent value="posts" className="mt-6"><Posts /></TabsContent>
        <TabsContent value="settings" className="mt-6"><Settings /></TabsContent>
      </Tabs>
    </div>
  );
}

function Members() {
  const [d, setD] = useState(null);
  const [q, setQ] = useState("");
  const load = () => api.get("/admin/community/members").then(({ data }) => setD(data));
  useEffect(() => { load(); }, []);
  if (!d) return <Loader2 className="h-8 w-8 animate-spin text-[#DCA846]" />;
  const suspend = async (uid, val) => { await api.post(`/admin/community/users/${uid}/suspend`, { suspended: val }); load(); };
  const adjust = async (uid, amount) => {
    const raw = prompt(`Adjust credits by (use negative to subtract):`, String(amount));
    if (raw === null) return;
    const val = parseInt(raw, 10);
    if (Number.isNaN(val) || val === 0) return;
    await api.post(`/admin/community/users/${uid}/credits`, { amount: val });
    toast.success(`Credits adjusted by ${val > 0 ? "+" : ""}${val}`);
    load();
  };
  const list = d.members.filter((m) => (m.display_name + m.username).toLowerCase().includes(q.toLowerCase()));
  return (
    <div>
      <div className="mb-4 flex gap-4 text-sm text-zinc-400">
        <span>Active members: <b className="text-white">{d.active}</b></span>
        <span>Total: <b className="text-white">{d.total}</b></span>
      </div>
      <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search members..." className="mb-4 max-w-sm bg-zinc-900 border-white/10 text-white" data-testid="admin-member-search" />
      <div className="overflow-x-auto rounded-lg border border-white/10">
        <table className="w-full text-sm">
          <thead className="bg-zinc-900 text-left text-xs uppercase text-zinc-400">
            <tr>
              <th className="p-3">Barber</th><th className="p-3">Credits</th><th className="p-3">Posts</th>
              <th className="p-3">Supports Given</th><th className="p-3">Status</th><th className="p-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {list.map((m) => (
              <tr key={m.id} className="border-t border-white/5" data-testid={`member-${m.user_id}`}>
                <td className="p-3">{m.display_name} <span className="text-zinc-500">@{m.username}</span></td>
                <td className="p-3"><span className="inline-flex items-center gap-1 font-display text-[#DCA846]"><Coins className="h-3.5 w-3.5" />{m.credits}</span></td>
                <td className="p-3">{m.posts}</td>
                <td className="p-3">{m.supports_given}</td>
                <td className="p-3">{m.suspended ? <span className="text-red-400">Suspended</span> : <span className="text-emerald-400">Active</span>}</td>
                <td className="p-3">
                  <div className="flex items-center justify-end gap-1">
                    <Button size="sm" variant="ghost" data-testid={`add-credits-${m.user_id}`} onClick={() => adjust(m.user_id, 5)} className="text-[#DCA846]" title="Add credits"><Plus className="h-4 w-4" /></Button>
                    <Button size="sm" variant="ghost" data-testid={`sub-credits-${m.user_id}`} onClick={() => adjust(m.user_id, -5)} className="text-zinc-400" title="Remove credits"><Minus className="h-4 w-4" /></Button>
                    {m.suspended
                      ? <Button size="sm" variant="ghost" data-testid={`restore-${m.user_id}`} onClick={() => suspend(m.user_id, false)} className="text-emerald-400"><Check className="mr-1 h-4 w-4" />Restore</Button>
                      : <Button size="sm" variant="ghost" data-testid={`suspend-${m.user_id}`} onClick={() => suspend(m.user_id, true)} className="text-red-400"><Ban className="mr-1 h-4 w-4" />Suspend</Button>}
                  </div>
                </td>
              </tr>
            ))}
            {list.length === 0 && <tr><td colSpan={6} className="p-6 text-center text-zinc-500">No members yet.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Posts() {
  const [posts, setPosts] = useState(null);
  const load = () => api.get("/admin/community/posts").then(({ data }) => setPosts(data));
  useEffect(() => { load(); }, []);
  if (!posts) return <Loader2 className="h-8 w-8 animate-spin text-[#DCA846]" />;
  const toggle = async (id, val) => { await api.post(`/admin/community/posts/${id}/disable`, { disabled: val }); load(); };
  const del = async (id) => { if (confirm("Delete this post?")) { await api.delete(`/admin/community/posts/${id}`); load(); } };
  return (
    <div className="overflow-x-auto rounded-lg border border-white/10">
      <table className="w-full text-sm">
        <thead className="bg-zinc-900 text-left text-xs uppercase text-zinc-400">
          <tr>
            <th className="p-3">Barber</th><th className="p-3">Instagram</th><th className="p-3">Category</th>
            <th className="p-3">Supports</th><th className="p-3">Status</th><th className="p-3 text-right">Actions</th>
          </tr>
        </thead>
        <tbody>
          {posts.map((p) => (
            <tr key={p.id} className={`border-t border-white/5 ${p.disabled ? "opacity-50" : ""}`} data-testid={`admin-post-${p.id}`}>
              <td className="p-3">{p.author_name} <span className="text-zinc-500">@{p.author_username}</span></td>
              <td className="p-3"><a href={p.instagram_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-[#DCA846] hover:underline"><Instagram className="h-3.5 w-3.5" />View</a></td>
              <td className="p-3 text-zinc-400">{p.category}</td>
              <td className="p-3">{p.support_count}</td>
              <td className="p-3">{p.disabled ? <span className="text-red-400">Disabled</span> : <span className="text-emerald-400">Active</span>}</td>
              <td className="p-3">
                <div className="flex items-center justify-end gap-1">
                  <Button size="sm" variant="ghost" data-testid={`toggle-post-${p.id}`} onClick={() => toggle(p.id, !p.disabled)} className="text-zinc-300">{p.disabled ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}</Button>
                  <Button size="sm" variant="ghost" data-testid={`delete-post-${p.id}`} onClick={() => del(p.id)} className="text-red-400"><Trash2 className="h-4 w-4" /></Button>
                </div>
              </td>
            </tr>
          ))}
          {posts.length === 0 && <tr><td colSpan={6} className="p-6 text-center text-zinc-500">No posts yet.</td></tr>}
        </tbody>
      </table>
    </div>
  );
}

function NumField({ label, value, onChange, hint }) {
  return (
    <div>
      <label className="text-xs uppercase text-zinc-400">{label}</label>
      <Input type="number" value={value} onChange={(e) => onChange(parseInt(e.target.value, 10) || 0)} className="mt-1 bg-zinc-900 border-white/10 text-white" />
      {hint && <p className="mt-1 text-xs text-zinc-500">{hint}</p>}
    </div>
  );
}

function Settings() {
  const [s, setS] = useState(null);
  useEffect(() => { api.get("/admin/community/settings").then(({ data }) => setS(data)); }, []);
  if (!s) return <Loader2 className="h-8 w-8 animate-spin text-[#DCA846]" />;
  const set = (k, v) => setS((p) => ({ ...p, [k]: v }));
  const save = async () => { await api.put("/admin/community/settings", s); toast.success("Community settings saved"); };
  return (
    <div className="max-w-2xl space-y-6 rounded-lg border border-white/10 bg-zinc-900/40 p-6">
      <div className="flex items-center justify-between border-b border-white/5 pb-4">
        <div><span className="text-sm font-medium">Community enabled</span><p className="text-xs text-zinc-500">Turn the whole support system on or off.</p></div>
        <Switch checked={!!s.enabled} onCheckedChange={(v) => set("enabled", v)} data-testid="toggle-community-enabled" />
      </div>

      <div>
        <p className="mb-3 font-display text-sm uppercase tracking-wide text-[#DCA846]">Credit Economy</p>
        <div className="grid grid-cols-2 gap-4">
          <NumField label="Credits earned per support" value={s.credits_per_support} onChange={(v) => set("credits_per_support", v)} hint="Reward for supporting a barber (+3 default)" />
          <NumField label="Credit cost per support received" value={s.credit_cost_received} onChange={(v) => set("credit_cost_received", v)} hint="Deducted from post owner (-1 default)" />
          <NumField label="Starting credits" value={s.starting_credits} onChange={(v) => set("starting_credits", v)} hint="Given to a new member" />
        </div>
      </div>

      <div>
        <p className="mb-3 font-display text-sm uppercase tracking-wide text-[#DCA846]">Anti-Abuse Limits</p>
        <div className="grid grid-cols-2 gap-4">
          <NumField label="Daily support limit" value={s.daily_support_limit} onChange={(v) => set("daily_support_limit", v)} hint="Max supports a member can give per day" />
          <NumField label="Daily post limit" value={s.daily_post_limit} onChange={(v) => set("daily_post_limit", v)} hint="Max posts a member can submit per day" />
          <NumField label="Support limit per post" value={s.support_limit_per_post} onChange={(v) => set("support_limit_per_post", v)} hint="Max supports a single post can receive" />
        </div>
      </div>

      <div>
        <label className="text-xs uppercase text-zinc-400">Membership mode</label>
        <Select value={s.mode} onValueChange={(v) => set("mode", v)}>
          <SelectTrigger className="mt-1 bg-zinc-900 border-white/10 text-white"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-zinc-950 border-white/10 text-white"><SelectItem value="FREE">Free</SelectItem><SelectItem value="PAID_MONTHLY">Paid Monthly</SelectItem></SelectContent>
        </Select>
        <p className="mt-1 text-xs text-zinc-500">Currently FREE — no student is charged. Members use the same account as their courses.</p>
      </div>

      <Button data-testid="save-community-settings" onClick={save} className="bg-[#DCA846] text-black hover:bg-[#C5963B]"><Save className="mr-1 h-4 w-4" /> Save Settings</Button>
    </div>
  );
}
